import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, X, Heart, User, AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptBeneficiary() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('token');
  const [beneficiaryId, setBeneficiaryId] = useState(searchParams.get('id'));
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [alreadyProcessed, setAlreadyProcessed] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [authName, setAuthName] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authedUser, setAuthedUser] = useState(null);

  useEffect(() => {
    loadInvitation();
  }, [beneficiaryId, inviteToken]);

  const loadInvitation = async () => {
    if (!beneficiaryId && !inviteToken) {
      setError('Invalid invitation link. Please check the link from your email.');
      setLoading(false);
      return;
    }

    try {
      let response;
      if (inviteToken) {
        response = await axios.get(`${BACKEND_URL}/api/beneficiaries/invitation-by-token/${inviteToken}`);
        if (response.data.beneficiary_id) setBeneficiaryId(response.data.beneficiary_id);
      } else {
        response = await axios.get(`${BACKEND_URL}/api/beneficiaries/invitation/${beneficiaryId}`);
      }
      setInvitation(response.data);

      // Check if already processed
      if (response.data.beneficiary.status !== 'pending') {
        setAlreadyProcessed(true);
      }
    } catch (error) {
      console.error('Failed to load invitation:', error);
      if (error.response?.status === 410) {
        setError(error.response?.data?.detail || 'This invitation link has expired or has already been used.');
      } else if (error.response?.status === 404) {
        setError('This invitation link is invalid or has expired. Please contact the person who sent you this invitation.');
      } else {
        setError('Failed to load invitation. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const invitedEmail = invitation?.beneficiary?.email || '';

  // Detect an existing signed-in session (e.g. after returning from Google) to enable
  // one-tap acceptance and to enforce that the signed-in email matches the invite.
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token || !invitation) return;
    axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAuthedUser(r.data))
      .catch(() => setAuthedUser(null));
  }, [invitation]);

  const handleGoogle = () => {
    // Come back to this exact invite after Google finishes.
    localStorage.setItem('post_auth_redirect', window.location.pathname + window.location.search);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOutAndRetry = () => {
    localStorage.removeItem('session_token');
    setAuthedUser(null);
    setAuthError('');
  };

  const handleResponse = async (accept) => {
    // Accepting now requires an account with the invited email (account-first).
    if (accept) {
      const token = localStorage.getItem('session_token');
      if (authedUser && token) {
        const match = (authedUser.email || '').toLowerCase() === invitedEmail.toLowerCase();
        if (!match) {
          setShowAuth(true);
          setAuthError(`You're signed in as ${authedUser.email}, but this invitation was sent to ${invitedEmail}. Please sign out and continue with ${invitedEmail}.`);
          return;
        }
        setProcessing(true);
        try {
          await completeAccept(token);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to accept invitation');
        } finally {
          setProcessing(false);
        }
        return;
      }
      setAuthName(invitation?.beneficiary?.name || '');
      setAuthError('');
      setShowAuth(true);
      return;
    }
    // Decline — no account required.
    setProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/beneficiaries/accept`, {
        beneficiary_id: beneficiaryId,
        accept: false,
        token: inviteToken || undefined,
      });
      if (response.data.already_processed) toast.info(response.data.message);
      else toast.success('Invitation declined');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process response');
    } finally {
      setProcessing(false);
    }
  };

  const completeAccept = async (token) => {
    const res = await axios.post(
      `${BACKEND_URL}/api/beneficiaries/accept`,
      { beneficiary_id: beneficiaryId, accept: true, token: inviteToken || undefined },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.data.already_processed) toast.info(res.data.message);
    else toast.success('🎉 Invitation accepted! Welcome.');
    setTimeout(() => navigate('/dashboard'), 1500);
  };

  const submitAuth = async () => {
    setAuthError('');
    if (!authPassword || authPassword.length < 6) {
      setAuthError('Password must be at least 6 characters.');
      return;
    }
    if (!isLogin && !authName.trim()) {
      setAuthError('Please enter your name.');
      return;
    }
    setProcessing(true);
    try {
      let token;
      if (isLogin) {
        const r = await axios.post(`${BACKEND_URL}/api/auth/login`, {
          email: invitedEmail, password: authPassword,
        });
        if (r.data.requires_2fa) {
          setAuthError('This account uses two-factor login. Please sign in from the login page, then reopen this link.');
          setProcessing(false);
          return;
        }
        token = r.data.token;
      } else {
        const r = await axios.post(`${BACKEND_URL}/api/auth/register`, {
          name: authName.trim(), email: invitedEmail, password: authPassword,
        });
        token = r.data.token;
      }
      localStorage.setItem('session_token', token);
      await completeAccept(token);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('already registered')) {
        setAuthError('An account with this email already exists. Please switch to "Sign in" below.');
        setIsLogin(true);
      } else {
        setAuthError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Loader2 className="h-12 w-12 text-[#2B4C7E] mx-auto" />
          </motion.div>
          <p className="text-[#2B4C7E] mt-4 font-medium">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5] p-6">
        <Card className="p-8 max-w-md w-full bg-white/90 backdrop-blur-md border border-red-100 shadow-xl rounded-3xl">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Invitation Error</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full px-8">
                Go to Home
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Already processed state
  if (alreadyProcessed && invitation) {
    const status = invitation.beneficiary.status;
    const isAccepted = status === 'accepted';
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5] p-6">
        <Card className="p-8 max-w-md w-full bg-white/90 backdrop-blur-md border border-[#EBF0F5] shadow-xl rounded-3xl">
          <div className="text-center">
            <div className={`w-20 h-20 ${isAccepted ? 'bg-green-100' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {isAccepted ? (
                <CheckCircle className="h-10 w-10 text-green-600" />
              ) : (
                <XCircle className="h-10 w-10 text-gray-500" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {isAccepted ? 'Already Accepted' : 'Invitation Declined'}
            </h1>
            <p className="text-gray-600 mb-6">
              {isAccepted 
                ? `You've already accepted the invitation from ${invitation.account_holder.name}. You can sign in to view your benefactor's information.`
                : `This invitation from ${invitation.account_holder.name} was previously declined.`
              }
            </p>
            <Link to="/login">
              <Button className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full px-8">
                Sign In
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5] p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-8 max-w-2xl w-full bg-white/90 backdrop-blur-md border border-[#EBF0F5] shadow-xl rounded-3xl">
          <div className="text-center mb-8">
            {/* Account Holder Info */}
            {invitation?.account_holder && (
              <div className="mb-6">
                {invitation.account_holder.profile_picture ? (
                  <img 
                    src={invitation.account_holder.profile_picture} 
                    alt={invitation.account_holder.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto ring-4 ring-[#EBF0F5]"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-[#3A5C8F] to-[#D0B384] rounded-full flex items-center justify-center mx-auto ring-4 ring-[#EBF0F5]">
                    <span className="text-2xl font-bold text-white">
                      {invitation.account_holder.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-[#2B4C7E]" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent mb-2">
              You've Been Chosen
            </h1>
            <p className="text-gray-600 text-lg">
              <strong>{invitation?.account_holder?.name || 'Someone special'}</strong> has added you as a beneficiary
            </p>
            
            {/* Role Badge */}
            {invitation?.beneficiary && (invitation.beneficiary.is_primary || invitation.beneficiary.is_secondary_primary) && (
              <div className="mt-3">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                  invitation.beneficiary.is_primary 
                    ? 'bg-[#F2E9D9] text-[#836A45]' 
                    : 'bg-[#F2E9D9] text-[#836A45]'
                }`}>
                  {invitation.beneficiary.is_primary ? '⭐ Primary Executor' : '🔸 Secondary Executor'}
                </span>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-[#EBF0F5] to-[#F7F1E8] rounded-2xl p-6 mb-8">
            <p className="text-gray-700 mb-4 font-medium">
              By accepting this invitation, you agree to be added as a beneficiary. This means you may receive:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-[#2B4C7E] mr-3 mt-0.5 flex-shrink-0" />
                <span>Personal messages ("Whispers") from {invitation?.account_holder?.name || 'the account holder'}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-[#2B4C7E] mr-3 mt-0.5 flex-shrink-0" />
                <span>Photo and video albums with cherished memories</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-[#2B4C7E] mr-3 mt-0.5 flex-shrink-0" />
                <span>Financial distributions (if designated)</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-[#2B4C7E] mr-3 mt-0.5 flex-shrink-0" />
                <span>Important documents and information</span>
              </li>
              {(invitation?.beneficiary?.is_primary || invitation?.beneficiary?.is_secondary_primary) && (
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-[#B3956B] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="font-medium text-[#836A45]">
                    Ability to trigger the "Easy Passing" process when the time comes
                  </span>
                </li>
              )}
            </ul>
          </div>

          {!showAuth ? (
            <>
              <div className="flex gap-4">
                <Button
                  onClick={() => handleResponse(false)}
                  disabled={processing}
                  variant="outline"
                  className="flex-1 rounded-full py-6 border-2 hover:bg-gray-50"
                  data-testid="decline-btn"
                >
                  <X className="h-5 w-5 mr-2" />
                  Decline
                </Button>
                <Button
                  onClick={() => handleResponse(true)}
                  disabled={processing}
                  className="flex-1 bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full py-6 hover:from-[#1E3A63] hover:to-[#9C7F55] shadow-lg"
                  data-testid="accept-btn"
                >
                  <Check className="h-5 w-5 mr-2" />
                  Accept Invitation
                </Button>
              </div>
              <p className="text-center text-sm text-gray-500 mt-6">
                To accept, you'll create a free account with <strong>{invitedEmail}</strong> so your access is secure and only yours.
              </p>
            </>
          ) : (
            <div className="bg-white rounded-2xl p-1" data-testid="beneficiary-auth-step">
              <h2 className="text-xl font-bold text-[#1C252E] mb-1">
                {isLogin ? 'Sign in to accept' : 'Create your account to accept'}
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                {isLogin
                  ? 'Sign in with the email this invitation was sent to.'
                  : 'Your account keeps your loved one’s messages private and secure.'}
              </p>

              {authedUser && (authedUser.email || '').toLowerCase() !== invitedEmail.toLowerCase() && (
                <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="beneficiary-email-mismatch">
                  <p className="text-sm text-amber-800">
                    You’re signed in as <strong>{authedUser.email}</strong>, but this invitation was sent to <strong>{invitedEmail}</strong>.
                  </p>
                  <button onClick={signOutAndRetry} className="mt-2 text-sm font-semibold text-[#2B4C7E] hover:underline" data-testid="beneficiary-signout-retry">
                    Sign out & use {invitedEmail}
                  </button>
                </div>
              )}

              <Button
                onClick={handleGoogle}
                variant="outline"
                className="w-full rounded-full py-6 border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50 mb-3"
                data-testid="beneficiary-google-btn"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-2" />
                Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-4">
                <div className="h-px bg-slate-200 flex-1" />
                <span className="text-xs text-slate-400">or use email &amp; password</span>
                <div className="h-px bg-slate-200 flex-1" />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Email (from your invitation)</label>
                  <input
                    type="email"
                    value={invitedEmail}
                    readOnly
                    disabled
                    className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-gray-600 mt-1"
                    data-testid="beneficiary-auth-email"
                  />
                </div>

                {!isLogin && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Your name</label>
                    <input
                      type="text"
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      placeholder="Full name"
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 text-sm mt-1 outline-none focus:border-[#2B4C7E]"
                      data-testid="beneficiary-auth-name"
                    />
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-500">Password</label>
                  <input
                    type="password"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitAuth(); }}
                    placeholder={isLogin ? 'Your password' : 'Create a password (min 6 characters)'}
                    className="w-full h-11 px-3 rounded-xl border border-slate-200 text-sm mt-1 outline-none focus:border-[#2B4C7E]"
                    data-testid="beneficiary-auth-password"
                  />
                </div>

                {authError && (
                  <p className="text-xs text-red-600" data-testid="beneficiary-auth-error">{authError}</p>
                )}

                <Button
                  onClick={submitAuth}
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full py-6 hover:from-[#1E3A63] hover:to-[#9C7F55] shadow-lg"
                  data-testid="beneficiary-auth-submit"
                >
                  {processing ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Please wait...</>
                  ) : (
                    <><Check className="h-5 w-5 mr-2" /> {isLogin ? 'Sign in & accept' : 'Create account & accept'}</>
                  )}
                </Button>

                <button
                  onClick={() => { setIsLogin(!isLogin); setAuthError(''); }}
                  className="w-full text-sm text-[#2B4C7E] hover:underline"
                  data-testid="beneficiary-auth-toggle"
                >
                  {isLogin ? "New here? Create an account instead" : 'Already have an account? Sign in'}
                </button>

                <button
                  onClick={() => { setShowAuth(false); setAuthError(''); setAuthPassword(''); }}
                  className="w-full text-xs text-gray-400 hover:text-gray-600"
                  data-testid="beneficiary-auth-back"
                >
                  ← Back
                </button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
